import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  TableSortLabel, 
  Autocomplete, 
  TextField, 
  Box, 
  Typography 
} from '@mui/material';
import ErrorDialog from '../ErrorDialog';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { rpc } from '../utils/RpcUtils';
import '../App.css';

const SiteList = () => {
  const [sites, setSites] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSites = async () => {
      rpc({
        method: "GetSites",
        params: {},
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: setSites
      });
    };
    fetchSites();
  }, []);

  // --- Helper Methods ---

  const createSite = () => {
    navigate(`/createSite`);
  };

  const handleClose = () => {
    setOpenErrorDialog(false);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // --- Navigation Handler for Autocomplete ---

  const handleAutocompleteSelect = (event, selectedSite) => {
    if (selectedSite && selectedSite._id) {
      navigate(`/site/${selectedSite._id}`);
    }
  };

  // --- Sorting Logic ---

  const sortedSites = sites.slice().sort((a, b) => {
    if (orderBy === 'name') {
      const nameA = a.name || "";
      const nameB = b.name || "";
      return order === 'asc' 
        ? nameA.localeCompare(nameB) 
        : nameB.localeCompare(nameA);
    }
    return 0;
  });

  return (
    <div className="SiteList" style={{ padding: '20px' }}>
      {/* Search and Actions Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3, 
        gap: 2,
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '8px',
        boxShadow: '0px 2px 4px rgba(0,0,0,0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', minWidth: 'fit-content' }}>
            Site Search:
          </Typography>
          <Autocomplete
            disablePortal
            id="site-search-select"
            options={sites}
            getOptionLabel={(option) => option.name || ""}
            sx={{ width: 400, backgroundColor: 'white' }}
            onChange={handleAutocompleteSelect}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Search for a site..." 
                variant="outlined" 
                size="small" 
              />
            )}
          />
        </Box>
        
        <Button 
          variant="contained" 
          color="primary" 
          onClick={createSite}
          sx={{ height: '40px', px: 4 }}
        >
          Create Site
        </Button>
      </Box>

      {/* Main Table */}
      <TableContainer className="TableContainer" component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  <b>Name</b>
                </TableSortLabel>
              </TableCell>
              <TableCell><b>Phone</b></TableCell>
              <TableCell><b>AMD</b></TableCell>
              <TableCell><b>Timezone</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSites.map((site) => (
              <TableRow key={site._id} hover>
                <TableCell>
                  <Link 
                    to={`/site/${site._id}`} 
                    style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 500 }}
                  >
                    {site.name}
                  </Link>
                </TableCell>
                <TableCell>{site.phone}</TableCell>
                <TableCell>{site.useAmd ? "Yes" : "No"}</TableCell>
                <TableCell>{site.timezone}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ErrorDialog 
        open={openErrorDialog} 
        onErrorDialogClose={handleClose} 
        errorMessage={errorMessage} 
      />
    </div>
  );
};

export default SiteList;